import { FormEvent, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppContext from '../../contexts/AppContext';
import ConfigContext from '../../contexts/ConfigContext';
import DataContext from '../../contexts/DataContext';

import AnswerOptions from '../../components/AnswerOptions';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { appendScript } from '../../helpers';
import states from '../../data/states';
import satelliteImage from './../../images/satellite-image.jpg';

import GoogleAutocomplete from '../components/GoogleAutocomplete';
import SlideHeading from '../components/SlideHeading';
import { address } from '../data/pages';
import { AddressOwnership } from '../enums';

export default function Address() {
  const {
    currentPageIndex,
    setCurrentPageIndex,
    setEnphaseAccTariffs,
    setUsedPanelOnTheRoofFailsafe,
    usedPanelOnTheRoofFailsafe,
    setStartedFunnel,
  } = useContext(AppContext);
  const configData = useContext(ConfigContext);
  const { features, updateConfigData } = configData;
  const { data, updateData } = useContext(DataContext);

  const [googleAddressHaveError, setGoogleAddressHaveError] = useState(false);
  const [addressHaveError, setAddressHaveError] = useState(false);
  const [stateHaveError, setStateHaveError] = useState(false);
  const [zipCodeHaveError, setZipHaveError] = useState(false);
  const [previousZipCode, setPreviousZipCode] = useState(data.selectedZipCode);
  const [googleAutocompleteActive, setGoogleAutocompleteActive] =
    useState(false);

  const { addressOwnership, selectedAddress, selectedState, selectedZipCode } =
    data;
  const {
    heading,
    inputData,
    nextPage,
    addressOwnership: addressOwnershipData,
  } = address;
  const { streetAddress, state, zipCode } = inputData;
  const { label: addressOwnershipLabel, options: addressOwnershipOptions } =
    addressOwnershipData;

  const [searchParams, setSearchParams] = useSearchParams();
  const isSatelliteTest = searchParams.get('test') === 'satellite';

  const isButtonDisabled =
    !addressOwnership ||
    !selectedAddress ||
    !selectedState ||
    !selectedZipCode ||
    addressHaveError ||
    stateHaveError ||
    zipCodeHaveError ||
    (features.useGoogleAutocomplete && googleAddressHaveError);

  useEffect(() => {
    const newAddressOwnership = addressOwnership || AddressOwnership.Own;

    // Set default data
    // TODO: make this a generic/global hook
    updateData({
      ...data,
      addressOwnership: newAddressOwnership,
      selectedAddress,
      selectedState,
      selectedZipCode,
    });

    setStartedFunnel(true);
  }, []);

  // const isValidZipCode = async (zipCode: String) => {
  //   const response = await fetch(
  //     `https://18kktizrg6.execute-api.us-east-1.amazonaws.com/staging/is-valid-zip-code?zipCode=${zipCode}`
  //   );
  //   const content = await response.json();
  //   return content.isValid;
  // };

  useEffect(() => {
    if (selectedZipCode && previousZipCode !== selectedZipCode) {
      if (features.useUtilityHeadline) {
        window.setGeoUtility = (utilityResponse) => {
          window.updateCity = (response) => {
            const selectedCity = response.data.placename || response.city;
            if (selectedCity) {
              updateData({
                ...data,
                selectedCity,
                utilityProvider: utilityResponse.utility || null,
              });
            } else {
              updateData({
                ...data,
                selectedCity: null,
                utilityProvider: utilityResponse.utility || null,
              });
            }
          };
          appendScript(
            `https://geoip.solarleadfactory.ai/zip2state/?callback=updateCity&zip=${data.selectedZipCode}`,
            true
          );
        };
        appendScript(
          `https://geoip.solarleadfactory.ai/utility2/?callback=setGeoUtility&zip=${data.selectedZipCode}`,
          true
        );
      }

      updateData({
        ...data,
        utilityProvider: null,
      });
      setEnphaseAccTariffs({});

      if (usedPanelOnTheRoofFailsafe) {
        updateConfigData({
          ...configData,
          features: { ...features, showPanelPlacement: true },
        });
        setUsedPanelOnTheRoofFailsafe(false);
      }

      setPreviousZipCode(selectedZipCode);
    }
  }, [selectedZipCode]);

  const handleAddressChange = (event: FormEvent<HTMLInputElement>) => {
    const eventValue = event.currentTarget.value;
    const haveError = !eventValue || eventValue.trim().length < 1;
    const newValue = haveError ? null : eventValue;

    updateData({
      ...data,
      selectedAddress: newValue,
      utilityProvider: null,
    });
    setAddressHaveError(haveError);
  };

  const handleStateChange = (event: FormEvent<HTMLInputElement>) => {
    const eventValue = event.currentTarget.value.trim();
    const invalidChar = !!(
      eventValue &&
      eventValue.length &&
      !eventValue[eventValue.length - 1].match(/[A-Za-z]/)
    );

    const haveError =
      !eventValue ||
      eventValue.length !== 2 ||
      invalidChar ||
      (eventValue.length === 2 &&
        !Object.values(states).includes(eventValue.toUpperCase()));

    const newValue = invalidChar ? selectedState : eventValue;

    updateData({
      ...data,
      selectedState: newValue,
      utilityProvider: null,
    });
    setStateHaveError(haveError);
  };

  const handleZipCodeChange = (event: FormEvent<HTMLInputElement>) => {
    const eventValue: string = event.currentTarget.value.trim();
    const haveError = !eventValue || eventValue.trim().length !== 5;
    updateData({ ...data, selectedZipCode: eventValue });
    setZipHaveError(haveError);
  };

  return (
    <>
      <SlideHeading heading={heading} />
      {isSatelliteTest ? (
        <>
          <p className="mt-[15px] md:mt-10 text-[16px] md:text-[18px]">
            We will use satellite imagery to assess your roof's sunlight
            potential.
          </p>
          <img
            className="satelliteImage md:width-[100%]"
            src={satelliteImage}
            alt="Satellite of homes"
          />
        </>
      ) : (
        <p className="mt-[30px] md:mt-10 text-[17px] short:!text-[16px] md:text-[18px]">
          Provide your address for estimate
        </p>
      )}
      {features.useGoogleAutocomplete ? (
        <div className="inline-flex flex-col short:!w-[350px] md:w-[400px] w-full mt-3 md:mt-4 mb-6 md:mb-10 relative">
          <GoogleAutocomplete
            hasError={googleAddressHaveError}
            setHasError={setGoogleAddressHaveError}
            setGoogleAutocompleteActive={setGoogleAutocompleteActive}
          />
        </div>
      ) : (
        <>
          <Input
            className="mt-3 md:mt-4 md:w-[400px]"
            defaultValue={selectedAddress}
            errorMessage={streetAddress.errorMessage}
            haveError={addressHaveError}
            name={streetAddress.label}
            onChange={handleAddressChange}
            placeholder={streetAddress.placeholder}
          />
          <div className="inline-flex md:w-[400px] mt-3 md:mt-4 mb-6 md:mb-10">
            <Input
              className="max-w-[112px] md:max-w-[167px] mr-[8px]"
              errorMessage={state.errorMessage}
              haveError={stateHaveError}
              maxLength={state.maxLength}
              name={state.label}
              onChange={handleStateChange}
              placeholder={state.placeholder}
              value={selectedState}
            />
            <Input
              className="max-w-[160px] md:max-w-[225px]"
              errorMessage={zipCode.errorMessage}
              haveError={zipCodeHaveError}
              maxLength={zipCode.maxLength}
              name={zipCode.label}
              onChange={handleZipCodeChange}
              placeholder={zipCode.placeholder}
              value={selectedZipCode}
            />
          </div>
        </>
      )}
      <p className="mb-4 text-[17px] short:!text-[16px] md:text-[18px]">
        {addressOwnershipLabel}
      </p>
      <div className="relative">
        <AnswerOptions
          className="relative"
          disabled={googleAutocompleteActive}
          options={addressOwnershipOptions}
          selectedOption={addressOwnership}
          updateSelectedOption={(selectedOption: string) => {
            updateData({ ...data, addressOwnership: selectedOption });
          }}
          slideKey="addressOwnership"
        />
        {googleAutocompleteActive && (
          <div className="absolute top-0 left-0 w-full h-full"></div>
        )}
      </div>
      <div className="mt-[25px] md:mt-[40px]">
        <Button
          action={() => {
            dataLayer.push({
              event: 'seform_address_entered',
              event_action: 'Do you own the property?',
              event_label: addressOwnership,
            });

            updateData({
              ...data,
              addressOwnership,
            });

            // set current page index
            setCurrentPageIndex(currentPageIndex + 1);

            // update page param and navigate to next page
            searchParams.set('page', nextPage);
            setSearchParams(searchParams);
          }}
          disabled={isButtonDisabled}
         // data-zuko-id="addressOwnership_continue"
        />
      </div>
    </>
  );
}
